import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DashboardVisibilityConfig, DEFAULT_VISIBILITY_CONFIG } from '../dashboard.types';
import logger from '../../../utils/logger';
import { updateUserPreferences } from '../../../services/userService';
import { SET_PREFERENCES } from '../../../actions/actionTypes';

const STORAGE_KEY = 'dashboard-visibility-config';

// Helper function to convert array format to object format
const convertArrayToConfig = (dashboardReportVisibility: any[]): DashboardVisibilityConfig => {
  if (!Array.isArray(dashboardReportVisibility) || dashboardReportVisibility.length === 0) {
    return DEFAULT_VISIBILITY_CONFIG;
  }
  
  const config: DashboardVisibilityConfig = {
    charts: {
      leadsBySource: false,
      leadsByLabel: false,
      salesFunnel: false,
      taskStatus: false,
      callLeaderboard: false,
      areaChart: false,
      userPerformance: false,
      leadAssignment: false,
      salesTarget: false,
      customFields: false,
      callLogs: false,
      userSync: false,
    },
    metrics: {
      secondaryMetrics: false,
      tabSpecificMetrics: false,
    },
    activities: {
      activityList: false,
    },
    reports: {
      callLeaderboard: false,
      callLogs: false,
      userPerformance: false,
      leadAssignment: false,
      salesFunnel: false,
      activitySummary: false,
      leadSource: false,
      leadByLabels: false,
      salesTarget: false,
      taskStatus: false,
      customFields: false,
      quotationStatus: false,
      userSyncStatus: false,
      trends: false,
    },
  };
  
  dashboardReportVisibility.forEach((item: any) => {
    if (item.category && item.component && typeof item.visible === 'boolean') {
      const category = item.category as keyof DashboardVisibilityConfig;
      const categoryConfig = config[category] as Record<string, boolean>;
      if (categoryConfig && categoryConfig.hasOwnProperty(item.component)) {
        categoryConfig[item.component] = item.visible;
      }
    }
  });
  
  // Merge with defaults to ensure all components are present
  return {
    charts: { ...DEFAULT_VISIBILITY_CONFIG.charts, ...config.charts },
    metrics: { ...DEFAULT_VISIBILITY_CONFIG.metrics, ...config.metrics },
    activities: { ...DEFAULT_VISIBILITY_CONFIG.activities, ...config.activities },
    reports: { ...DEFAULT_VISIBILITY_CONFIG.reports, ...config.reports },
  };
};

// Helper function to convert object format to array format for API
const convertConfigToArray = (config: DashboardVisibilityConfig): any[] => {
  const result: any[] = [];
  
  Object.entries(config).forEach(([category, components]) => {
    Object.entries(components).forEach(([component, visible]) => {
      result.push({
        category,
        component,
        visible
      });
    });
  });
  
  return result;
};

export const useDashboardVisibility = () => {
  const [visibilityConfig, setVisibilityConfig] = useState<DashboardVisibilityConfig>(DEFAULT_VISIBILITY_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  // Get user preferences from Redux store
  const userPreferences = useSelector((state: any) => state?.rootReducers?.user?.userPreferences);

  // Load configuration from backend user preferences
  useEffect(() => {
    logger.log('🔍 [useDashboardVisibility] Loading config...');
    logger.log('🔍 [useDashboardVisibility] userPreferences:', userPreferences);

    try {
      if (userPreferences && userPreferences.dashboardReportVisibility) {
        // Check if backend data is object or array format
        const backendData = userPreferences.dashboardReportVisibility;
        logger.log('🔍 [useDashboardVisibility] Backend data:', backendData);

        let backendConfig: DashboardVisibilityConfig;

        if (Array.isArray(backendData)) {
          // Legacy array format - convert to object
          backendConfig = convertArrayToConfig(backendData);
          logger.log('🔄 [useDashboardVisibility] Converting legacy array format to object');
        } else {
          // New object format - use directly with fallback to defaults
          backendConfig = {
            charts: { ...DEFAULT_VISIBILITY_CONFIG.charts, ...backendData.charts },
            metrics: { ...DEFAULT_VISIBILITY_CONFIG.metrics, ...backendData.metrics },
            activities: { ...DEFAULT_VISIBILITY_CONFIG.activities, ...backendData.activities },
            reports: { ...DEFAULT_VISIBILITY_CONFIG.reports, ...backendData.reports },
          };
        }

        logger.log('✅ [useDashboardVisibility] Config loaded from backend:', backendConfig);
        logger.log('📊 [useDashboardVisibility] Reports config:', backendConfig.reports);

        setVisibilityConfig(backendConfig);
        logger.log('🌐 Dashboard visibility config loaded from backend:', backendConfig);
        logger.log('🔍 Backend data format:', backendData);
      } else {
        console.warn('⚠️ [useDashboardVisibility] No backend config, checking localStorage...');

        // Fallback to localStorage if backend doesn't have it yet
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          const merged = {
            ...DEFAULT_VISIBILITY_CONFIG,
            charts: { ...DEFAULT_VISIBILITY_CONFIG.charts, ...parsed.charts },
            metrics: { ...DEFAULT_VISIBILITY_CONFIG.metrics, ...parsed.metrics },
            activities: { ...DEFAULT_VISIBILITY_CONFIG.activities, ...parsed.activities },
            reports: { ...DEFAULT_VISIBILITY_CONFIG.reports, ...parsed.reports },
          };
          logger.log('📱 [useDashboardVisibility] Config loaded from localStorage:', merged);
          setVisibilityConfig(merged);
          logger.log('📱 Dashboard visibility config loaded from localStorage fallback', merged);
        } else {
          logger.log('📱 [useDashboardVisibility] No stored config, using defaults');
          setVisibilityConfig(DEFAULT_VISIBILITY_CONFIG);
          logger.log('📱 No stored config found, using defaults');
        }
      }
    } catch (error) {
      console.error('❌ [useDashboardVisibility] Error loading dashboard config:', error);
      logger.log('❌ Error loading dashboard config:', error);
      setVisibilityConfig(DEFAULT_VISIBILITY_CONFIG);
    } finally {
      setIsLoading(false);
    }
  }, [userPreferences]);

  // Save configuration to backend and localStorage
  const saveConfig = useCallback(async (config: DashboardVisibilityConfig) => {
    try {
      // Update local state immediately for UI responsiveness
      setVisibilityConfig(config);

      // Save to localStorage as backup
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));

      // Send config object directly (API expects object, not array)
      const updateData = {
        dashboardReportVisibility: config
      };

      logger.log('🌐 [useDashboardVisibility] Saving dashboard visibility to backend:', updateData);
      logger.log('📊 [useDashboardVisibility] Reports config:', config.reports);

      await updateUserPreferences(updateData);

      // Update Redux store with new preferences
      const updatedPreferences = {
        ...userPreferences,
        dashboardReportVisibility: config
      };
      dispatch({ type: SET_PREFERENCES, payload: updatedPreferences });
      logger.log('✅ [useDashboardVisibility] Redux store updated with new preferences');
      logger.log('📊 [useDashboardVisibility] Updated Redux preferences:', updatedPreferences);

      logger.log('💾 [useDashboardVisibility] Dashboard visibility config saved successfully!', config);
      logger.log('🔄 [useDashboardVisibility] Please REFRESH the page to see auto-open reports update');

      logger.log('💾 Dashboard visibility config saved to backend and localStorage', config);
    } catch (error) {
      console.error('❌ [useDashboardVisibility] Error saving dashboard config to backend:', error);
      logger.log('❌ Error saving dashboard config to backend:', error);
      // Still save locally even if backend fails
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    }
  }, [dispatch, userPreferences]);

  // Update specific component visibility
  const updateComponentVisibility = useCallback((
    category: keyof DashboardVisibilityConfig,
    componentKey: string,
    isVisible: boolean
  ) => {
    const newConfig = {
      ...visibilityConfig,
      [category]: {
        ...visibilityConfig[category],
        [componentKey]: isVisible,
      },
    };
    saveConfig(newConfig);
  }, [visibilityConfig, saveConfig]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    saveConfig(DEFAULT_VISIBILITY_CONFIG);
  }, [saveConfig]);

  // Get enabled components count
  const getEnabledCount = useCallback(() => {
    const allComponents = {
      ...visibilityConfig.charts,
      ...visibilityConfig.metrics,
      ...visibilityConfig.activities,
      ...visibilityConfig.reports,
    };
    return Object.values(allComponents).filter(Boolean).length;
  }, [visibilityConfig]);

  // Get total components count
  const getTotalCount = useCallback(() => {
    const allComponents = {
      ...DEFAULT_VISIBILITY_CONFIG.charts,
      ...DEFAULT_VISIBILITY_CONFIG.metrics,
      ...DEFAULT_VISIBILITY_CONFIG.activities,
      ...DEFAULT_VISIBILITY_CONFIG.reports,
    };
    return Object.keys(allComponents).length;
  }, []);

  // Check if a specific component should be rendered
  const shouldRenderComponent = useCallback((
    category: keyof DashboardVisibilityConfig,
    componentKey: string
  ) => {
    const categoryConfig = visibilityConfig[category] as Record<string, boolean>;
    return categoryConfig[componentKey] ?? true; // Default to true if not found
  }, [visibilityConfig]);

  return {
    visibilityConfig,
    isLoading,
    saveConfig,
    updateComponentVisibility,
    resetToDefaults,
    getEnabledCount,
    getTotalCount,
    shouldRenderComponent,
  };
};